import { Column } from 'primereact/column'
import { DataTable, DataTablePageEvent, SortOrder } from 'primereact/datatable'
import { FC, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { LazyTableState, SelectOption } from '../../types/types'

import Papa from 'papaparse'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { classNames } from 'primereact/utils'
import { Controller, useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import {
  useGetReport,
  useGetReportCount,
  useGetReportFile,
  useGetUtilizationSummary
} from '../../api/report-API'
import {
  useGetComplianceCaseCount,
  useUpdateWorkflowSubmissionCompliance
} from '../../api/submission-API'
import {
  ComplianceDescOptions,
  ComplianceTeamOptions,
  GMMSRaceTeamKey,
  NASCARTeams
} from '../../utils/constants'
import { getFormErrorMessage } from '../../utils/form'
import { DropdownStyle } from '../../utils/styling-constants'
import CloseButton from '../buttons/CloseButton'
import { FilterIcon, LoadingIcon } from '../Icons'
import AgDialChart from './AgDialChart'

const pageSizeOptions = [50, 100]
const monthOptions: SelectOption[] = [
  { label: 'All Months', value: 'all' },
  { label: 'January', value: '01' },
  { label: 'February', value: '02' },
  { label: 'March', value: '03' },
  { label: 'April', value: '04' },
  { label: 'May', value: '05' },
  { label: 'June', value: '06' },
  { label: 'July', value: '07' },
  { label: 'August', value: '08' },
  { label: 'September', value: '09' },
  { label: 'October', value: '10' },
  { label: 'November', value: '11' },
  { label: 'December', value: '12' }
] as SelectOption[]

const availableFields = [
  {
    header: 'Run',
    field: 'run',
    width: 60,
    position: 10
  },
  // identifier
  {
    header: 'Identifier',
    field: 'NASCAR_Identifier',
    width: 100,
    position: 20
  },
  // state name
  {
    header: 'State Name',
    field: 'state_name',
    width: 120,
    position: 30
  },
  // compliance team
  {
    header: 'Org',
    field: 'compliance_team',
    width: 60,
    position: 40
  },
  // compliance description
  {
    header: 'Compliance Desc',
    field: 'compliance_description',
    width: 150,
    position: 50
  },
  // Start Date
  {
    header: 'Start Date',
    field: 'start_time',
    width: 110,
    position: 60
  },
  // Sum of Processors
  {
    header: 'Processors',
    field: 'processors',
    width: 100,
    position: 70
  },
  // Sum of Wall Time (h)
  {
    header: 'Wall Time (h)',
    field: 'wall_time_hr_billed',
    width: 120,
    position: 80
  },
  //Sum of CPU Hours
  {
    header: 'CPU Hours',
    field: 'cpu_hours_billed',
    width: 100,
    position: 90
  },
  // HPC Asset
  {
    header: 'HPC Asset',
    field: 'NASCAR_HPC_Asset',
    width: 100,
    position: 100
  },
  // NASCAR_Solution_Software
  {
    header: 'Solution Software',
    field: 'NASCAR_Solution_Software',
    width: 140,
    position: 110
  },
  // NASCAR_Input_Geometry
  {
    header: 'Input Geometry',
    field: 'NASCAR_Input_Geometry',
    width: 140,
    position: 120
  },
  // NASCAR_Solution_Outputs
  {
    header: 'Solution Outputs',
    field: 'NASCAR_Solution_Outputs',
    position: 130
  }
]

const currentYear = new Date().getFullYear()
const currentMonth = (new Date().getMonth() + 1).toString().padStart(2, '0')
const yearOptions: SelectOption[] = [
  { label: (currentYear - 1).toString(), value: (currentYear - 1).toString() },
  { label: currentYear.toString(), value: currentYear.toString() }
]

type ComplianceFormData = {
  run: string
  team: string
  description: string
}

const CfdReport: FC = () => {
  const countRef = useRef<number>(0)
  const [exporting, setExporting] = useState(false)
  const [selectedRun, setSelectedRun] = useState<ComplianceFormData | undefined>(undefined)
  const [confirmData, setConfirmData] = useState<
    { formData: ComplianceFormData; caseCount: number } | undefined
  >(undefined)
  const [{ loading: loadingReport }, getReport] = useGetReport()
  const [, getReportFile] = useGetReportFile()
  const [, getReportCount] = useGetReportCount()
  const [{ loading: loadingSummary }, getUtilizationSummary] = useGetUtilizationSummary()
  const [{ loading: updatingCompliance }, updateWorkflowSubmissionCompliance] =
    useUpdateWorkflowSubmissionCompliance()
  const [{ loading: loadingCaseCount }, getComplianceCaseCount] = useGetComplianceCaseCount()
  const [sortField, setSortField] = useState<string | undefined>('run')
  const [sortOrder, setSortOrder] = useState<SortOrder>(1)
  const teamId = localStorage.getItem(GMMSRaceTeamKey)

  const [columns] = useState(() => {
    const colDefs = availableFields.filter(field => {
      return (
        NASCARTeams.includes(teamId) ||
        (field.field !== 'compliance_team' && field.field !== 'compliance_description')
      )
    })
    return colDefs.map(field => ({
      ...field,
      sortable: true,
      headerStyle: {
        minWidth: field.width ? `${field.width}px` : 'auto'
      },
      body:
        field.field === 'run'
          ? (rowData: any) => {
              return (
                <Link to={`/submissions/${rowData.run}`} className="text-gm-blue hover:underline">
                  {rowData.run}
                </Link>
              )
            }
          : field.field === 'compliance_team'
            ? (rowData: any) => {
                const team = rowData.compliance_team || rowData.org || 'N/A'
                return (
                  <button
                    className="bg-transparent border-none p-0 text-left w-full cursor-pointer hover:underline hover:text-gm-blue dark:hover:text-gm-blue transition-all decoration-2 underline-offset-2"
                    onClick={() => {
                      setSelectedRun({
                        run: rowData.run,
                        team: rowData.compliance_team,
                        description: rowData.compliance_description
                      })
                      console.log('Selected run for editing:', rowData.run, rowData.runConfig)
                      setValue('team', rowData.compliance_team || '')
                      setValue('description', rowData.compliance_description || '')
                    }}
                    aria-label={`Edit compliance info for ${rowData.run}`}
                    title="Click to edit compliance information">
                    {team}
                  </button>
                )
              }
            : field.field === 'compliance_description'
              ? (rowData: any) => {
                  const description = rowData.compliance_description || 'N/A'
                  return (
                    <button
                      className="bg-transparent border-none p-0 text-left w-full cursor-pointer hover:underline hover:text-gm-blue dark:hover:text-gm-blue transition-all decoration-2 underline-offset-2"
                      onClick={() => {
                        setSelectedRun({
                          run: rowData.run,
                          team: rowData.compliance_team,
                          description: rowData.compliance_description
                        })
                        console.log('Selected run for editing:', rowData.run, rowData.runConfig)
                        setValue('team', rowData.compliance_team || '')
                        setValue('description', rowData.compliance_description || '')
                      }}
                      aria-label={`Edit compliance info for ${rowData.run}`}
                      title="Click to edit compliance information">
                      {description}
                    </button>
                  )
                }
              : undefined,
      headerClassName: `!pt-2 px-1`,
      bodyStyle: {},
      bodyClassName: '!pl-3'
    }))
  })
  const isMultiOrgTeam = teamId === 'NASCAR-Cup' || teamId === 'testing-team-dev'
  const [utilizationSummary, setUtilizationSummary] = useState<any[]>(() => {
    return isMultiOrgTeam
      ? ComplianceTeamOptions.map(team => ({
          team: team.value,
          completedRuns: 0,
          runLimit: 100 // hardcoded for now
        }))
      : [
          {
            team: '',
            completedRuns: 0,
            runLimit: 100 // hardcoded for now
          }
        ]
  })
  const [reportData, setReportData] = useState([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [complianceTeams] = useState<SelectOption[]>([
    { label: 'All Organizations', value: 'all' },
    ...ComplianceTeamOptions
  ])
  const [selectedOrganization, setSelectedOrganization] = useState<string>('all')
  const [selectedMonth, setSelectedMonth] = useState<string>(currentMonth)
  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString())
  const [lazyState, setlazyState] = useState<LazyTableState>({
    first: 0,
    page: 0,
    rows: pageSizeOptions[1],
    filters: {
      month: { value: currentMonth, matchMode: 'equals' },
      year: { value: currentYear.toString(), matchMode: 'equals' },
      org: { value: 'all', matchMode: 'equals' }
    },
    sortOrder: 1,
    sortField: undefined,
    sumlvl: 'detail'
  } as LazyTableState)

  const handleComplianceFormSubmit = async (formData: ComplianceFormData) => {
    try {
      const res = (await getComplianceCaseCount({
        runName: selectedRun?.run
      } as any)) as any
      const caseCount = res?.data?.caseCount ?? 0
      setConfirmData({ formData, caseCount })
    } catch (error) {
      console.error('Error fetching case count:', error)
      // If we can't get the count, still show confirmation with unknown count
      setConfirmData({ formData, caseCount: 0 })
    }
  }

  const handleConfirmedComplianceUpdate = async () => {
    if (!confirmData) return
    const { formData } = confirmData
    try {
      await updateWorkflowSubmissionCompliance({
        runName: selectedRun?.run,
        team: formData.team,
        desc: formData.description
      } as any)
      setSelectedRun(undefined)
      setConfirmData(undefined)
      toast.success(
        'Compliance information updated successfully, please allow a few minutes for the changes to take effect.'
      )
    } catch (error) {
      console.error('Error updating compliance info:', error.response || error)
      toast.error(error.response?.data || error || 'Unknown error')
    }
  }

  const downloadReport = async (data: Blob, year: string, month: string, org: string) => {
    try {
      let text
      if (typeof data !== 'string') {
        text = await data.text()
      } else {
        text = data as string
      }
      const parsed = Papa.parse(text, { header: true, skipEmptyLines: true })

      // Filter out any rows that don't have data (Papa sometimes includes empty rows)
      const validRows = (parsed.data as Record<string, any>[]).filter(row => {
        return Object.values(row).some(val => val !== null && val !== undefined && val !== '')
      })

      const normalizedSortOrder = sortOrder === -1 ? -1 : 1
      const sortedRows = [...validRows].sort((a, b) => {
        if (!sortField) return 0

        const aValue = a?.[sortField]
        const bValue = b?.[sortField]

        if (aValue == null && bValue == null) return 0
        if (aValue == null) return 1
        if (bValue == null) return -1

        const aNumber = Number(aValue)
        const bNumber = Number(bValue)
        const bothNumeric = Number.isFinite(aNumber) && Number.isFinite(bNumber)
        if (bothNumeric) {
          return (aNumber - bNumber) * normalizedSortOrder
        }

        const aDate = Date.parse(String(aValue))
        const bDate = Date.parse(String(bValue))
        const bothDates = Number.isFinite(aDate) && Number.isFinite(bDate)
        if (bothDates) {
          return (aDate - bDate) * normalizedSortOrder
        }

        return (
          String(aValue).localeCompare(String(bValue), undefined, {
            sensitivity: 'base',
            numeric: true
          }) * normalizedSortOrder
        )
      })

      // Process data and rename headers to match the display names
      const processedData = sortedRows.map((row: any) => {
        const filteredRow: any = {}
        const colDefs = availableFields.filter(field => {
          return (
            NASCARTeams.includes(teamId) ||
            (field.field !== 'compliance_team' && field.field !== 'compliance_description')
          )
        })

        colDefs.forEach(item => {
          // Map the database field name to the display header name
          const customHeader = item.header
          filteredRow[customHeader] = row[item.field] ?? ''
        })
        return filteredRow
      })
      // Convert back to CSV
      const csvData = Papa.unparse(processedData)
      const href = URL.createObjectURL(new Blob([csvData], { type: 'text/csv' }))

      // create "a" HTML element with href to file & click
      const link = document.createElement('a')
      link.href = href
      const team = teamId || 'unknown'
      const today = new Date().toISOString().slice(0, 10)
      const fileName = isMultiOrgTeam
        ? `${team}-${org && org !== 'all' ? org : 'multi'}-compliance-report-${today}.csv`
        : `${team}-compliance-report-${today}.csv`
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      // simulate clicking to download the file
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(href)
    } catch (error) {
      console.error('Error downloading CSV:', error)
      toast.error('Error downloading CSV')
    }
  }

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<ComplianceFormData>()

  useEffect(() => {
    // Fetch report count
    const reloadData = async () => {
      // fetch utilization summary
      try {
        const summaryRes = (await getUtilizationSummary({
          series: 'nascar',
          month: lazyState.filters.month['value'],
          year: lazyState.filters.year['value']
        } as any)) as any
        const data = summaryRes.data
        if (data && data.length > 0) {
          // Merge server data with initial state to preserve all teams
          setUtilizationSummary(prevSummary => {
            const updated = [...prevSummary]
            if (isMultiOrgTeam) {
              data.forEach((serverItem: any) => {
                // Match by team property - server might return different property names
                const teamValue = serverItem.team || serverItem.compliance_team || serverItem.org
                const index = updated.findIndex(item => item.team === teamValue)
                if (index !== -1) {
                  // Extract run count and ensure it's a number (server returns string)
                  const runCount =
                    serverItem.completedRuns ||
                    serverItem.completed_runs ||
                    serverItem.run_count ||
                    0
                  updated[index] = {
                    ...updated[index],
                    completedRuns: typeof runCount === 'string' ? Number(runCount) : runCount,
                    runLimit: serverItem.runLimit || serverItem.run_limit || updated[index].runLimit
                  }
                } else {
                  // If server returns a team not in our list, add it
                  const runCount =
                    serverItem.completedRuns ||
                    serverItem.completed_runs ||
                    serverItem.run_count ||
                    0
                  updated.push({
                    team: teamValue,
                    completedRuns: typeof runCount === 'string' ? Number(runCount) : runCount,
                    runLimit: serverItem.runLimit || serverItem.run_limit || 100
                  })
                }
              })
            } else {
              // For non-Nascar teams, just take the first item returned by the server
              const serverItem = data[0]
              const runCount =
                serverItem.completedRuns || serverItem.completed_runs || serverItem.run_count || 0
              updated[0] = {
                team: '',
                completedRuns: typeof runCount === 'string' ? Number(runCount) : runCount,
                runLimit: serverItem.runLimit || serverItem.run_limit || 100
              }
            }
            return updated
          })
        } else {
          // If no data returned, reset counts to 0
          setUtilizationSummary(prevSummary =>
            prevSummary.map(item => ({ ...item, completedRuns: 0 }))
          )
        }
      } catch (error) {
        console.error('Error fetching utilization summary:', error)
        toast.error('Error fetching utilization summary')
      }

      // fetch report count
      try {
        const countRes = (await getReportCount({
          org: lazyState.filters.org['value'],
          series: 'nascar',
          month: lazyState.filters.month['value'],
          year: lazyState.filters.year['value']
        } as any)) as any
        const data = countRes.data
        if (data.status.state === 'SUCCEEDED') {
          countRef.current = data.result.data_array[0][0]
        }
      } catch (error) {
        console.error('Error fetching report count:', error)
        toast.error('Error fetching report count')
      }

      try {
        // Fetch report data
        const reportRes = (await getReport({
          org: lazyState.filters.org['value'],
          series: 'nascar',
          page: lazyState.page,
          pageSize: lazyState.rows,
          month: lazyState.filters.month['value'],
          year: lazyState.filters.year['value']
        } as any)) as any
        const data = reportRes.data
        if (data && data.length) {
          countRef.current = data.length
          setTotalRecords(countRef.current)
          // Ensure each row has a truly unique key - combine index with run and page to handle duplicates
          const dataWithKeys = data.map((row: any, idx: number) => ({
            ...row,
            _rowKey: `${lazyState.page}-${idx}-${row.run || 'unknown'}`
          }))
          setReportData(dataWithKeys)
        } else {
          countRef.current = 0
          setTotalRecords(countRef.current)
          setReportData([])
        }
        //setColumns(cols)
      } catch (error) {
        console.error('Error fetching report data:', error)
        toast.error('Error fetching report data')
      }
    }

    reloadData()
  }, [lazyState])

  const handleDataPage = (event: DataTablePageEvent) => {
    setlazyState({
      ...lazyState,
      first: event.first,
      page: event.page as number,
      rows: event.rows
    })
  }

  return (
    <div className="flex flex-col items-stretch w-full p-2 space-y-4">
      <div className="flex items-center justify-between">
        <div />
        <Link
          to="/solve-count-compliance"
          className="text-sm text-gm-blue hover:underline font-semibold">
          View Solve Count Compliance →
        </Link>
      </div>
      <div className="flex flex-col p-6 border border-gray-100 border-solid rounded-xl space-y-4">
        <div className="text-lg text-gray-600">
          {'Resource Utilization' + (isMultiOrgTeam ? ' by Organization' : '')}
        </div>
        <div className="flex items-center space-x-8">
          {utilizationSummary.map((item, index) => {
            return (
              <div
                key={`${item.team || 'team'}-${index}`}
                className="flex flex-col items-stretch justify-center p-4 rounded-lg bg-dark-blue-500 space-y-4 w-[250px]">
                <div className="text-white text-center">{item.team}</div>
                <div className="flex items-center justify-center">
                  <AgDialChart
                    label={item.team}
                    value={item.completedRuns}
                    max={item.runLimit}
                    loading={loadingSummary}
                  />
                </div>
                <div className="flex items-center">
                  <div className="text-aqua-500 basis-0 grow text-left">0</div>
                  <div className="text-white basis-0 grow text-center">
                    {loadingSummary ? '' : item.completedRuns}
                  </div>
                  <div className="text-aqua-500  basis-0 grow text-right">{item.runLimit}</div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="flex flex-col items-stretch w-full p-4 border border-neutral-200 dark:border-neutral-700 border-solid rounded-xl space-y-4">
        <div className="flex flex-row justify-between pt-4 w-full">
          <h1 className="text-xl">NASCAR Report Output - Detailed View</h1>
          <div>
            <Button
              className="flex justify-between disabled:opacity-50"
              disabled={exporting || loadingReport || totalRecords === 0}
              onClick={async () => {
                if (Number(totalRecords) === 0) {
                  toast.error('No data to export')
                  return
                }
                setExporting(true)
                try {
                  const month = lazyState.filters.month['value']
                  const year = lazyState.filters.year['value']
                  const org = lazyState.filters.org['value']
                  const linkRes = await getReportFile({ series: 'nascar', month, year, org } as any)
                  if (linkRes.status === 200 && linkRes.data) {
                    downloadReport(linkRes.data, year, month, org)
                  } else {
                    toast.error('Error exporting report')
                  }
                } catch (error) {
                  console.error('Error exporting report:', error)
                  toast.error('Error exporting report')
                } finally {
                  setExporting(false)
                }
              }}>
              {exporting ? (
                <LoadingIcon className="h-4 w-4 fill-white animate-spin mr-2" />
              ) : (
                <i className="pi pi-download h-4 w-4 text-white mr-2" />
              )}
              <span>Export to Excel</span>
            </Button>
          </div>
        </div>

        <div className="flex items-center space-x-8 p-4">
          <div className="flex items-center space-x-2">
            <FilterIcon className="h-5 w-5 fill-neutral-600 dark:fill-neutral-200 mr-2 inline-block fill-gm-blue stroke-gm-blue" />
            Filters:
          </div>
          <div className="flex items-center space-x-2">
            {isMultiOrgTeam && (
              <>
                <span>Organization:</span>
                <Dropdown
                  disabled={loadingReport || exporting}
                  value={selectedOrganization}
                  pt={{
                    root: {
                      className: 'bg-transparent dark:border-neutral-70 dark:bg-transparent'
                    },
                    input: {
                      className: 'py-2'
                    }
                  }}
                  onChange={e => {
                    setSelectedOrganization(e.value)
                  }}
                  options={complianceTeams}
                  placeholder="Select Organization"
                  optionLabel="label"
                  optionValue="value"
                  emptyMessage="&nbsp;&nbsp;No data"
                />
              </>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <div>Month:</div>
            <Dropdown
              disabled={loadingReport || exporting}
              value={selectedMonth}
              pt={{
                root: {
                  className: 'bg-transparent dark:border-neutral-70 dark:bg-transparent'
                },
                input: {
                  className: 'py-2'
                }
              }}
              onChange={e => {
                setSelectedMonth(e.value)
              }}
              options={monthOptions}
              placeholder="Select Month"
              optionLabel="label"
              optionValue="value"
              emptyMessage="&nbsp;&nbsp;No data"
            />
          </div>
          <div className="flex items-center space-x-2">
            <div>Year:</div>
            <Dropdown
              disabled={loadingReport || exporting}
              value={selectedYear}
              pt={{
                root: {
                  className: 'bg-transparent dark:border-neutral-70 dark:bg-transparent'
                },
                input: {
                  className: 'py-2'
                }
              }}
              onChange={e => {
                setSelectedYear(e.value)
              }}
              options={yearOptions}
              placeholder="Select Year"
              optionLabel="label"
              optionValue="value"
              emptyMessage="&nbsp;&nbsp;No data"
            />
          </div>
          <div>
            <Button
              label="Apply"
              title="Apply Filter"
              loading={loadingReport}
              disabled={loadingReport || exporting}
              icon={<FilterIcon className="h-4 w-4 fill-white mr-2" />}
              pt={{
                loadingIcon: { className: 'h-4 w-4 fill-white mr-2' }
              }}
              onClick={() => {
                setlazyState({
                  ...lazyState,
                  page: 0,
                  first: 0,
                  filters: {
                    ...lazyState.filters,
                    month: { value: selectedMonth, matchMode: 'equals' },
                    year: { value: selectedYear, matchMode: 'equals' },
                    org: { value: selectedOrganization, matchMode: 'equals' }
                  }
                })
              }}
            />
          </div>
          <div className="basis-0 flex-grow flex items-center justify-end">
            {'Total: ' + totalRecords}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto max-w-full">
        <DataTable
          scrollable
          scrollHeight="calc(100vh - 440px)"
          value={reportData}
          dataKey="_rowKey"
          filterDisplay="row"
          stripedRows
          className="w-full"
          emptyMessage={() => {
            return reportData.length === 0 ? (
              <div key="empty" className="w-full p-4 text-center">
                No data found.
              </div>
            ) : null
          }}
          paginator={lazyState.rows > 0 && lazyState.rows < totalRecords}
          onPage={handleDataPage}
          rows={lazyState.rows}
          first={lazyState.first}
          totalRecords={totalRecords}
          rowsPerPageOptions={pageSizeOptions}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={e => {
            setSortField(e.sortField)
            setSortOrder(e.sortOrder)
          }}
          pt={{
            root: {
              className: 'mt-2'
            },
            thead: {
              className: 'sticky top-0 z-10 dark:bg-neutral-80'
            },
            bodyRow: {
              className: 'table-row'
            },
            paginator: {
              root: {
                className: 'justify-end'
              },
              RPPDropdown: {
                root: {
                  className: 'max-w-[120px]'
                }
              },
              prevPageButton: {
                className: 'paginator-prev-page-button'
              }
            }
          }}>
          {columns.map(item => {
            return (
              <Column
                key={item.field}
                field={item.field}
                header={item.header}
                sortable={item.sortable}
                headerStyle={item.headerStyle}
                bodyStyle={item.bodyStyle}
                headerClassName={item.headerClassName}
                bodyClassName={item.bodyClassName}
                body={item.body}></Column>
            )
          })}
        </DataTable>
      </div>

      <Dialog
        header={`Update compliance information for ${selectedRun && selectedRun.run}`}
        visible={!!selectedRun}
        closeOnEscape
        onHide={() => {
          if (!selectedRun) return
          setSelectedRun(undefined)
        }}>
        <form
          onSubmit={handleSubmit(handleComplianceFormSubmit)}
          className="w-full flex flex-col items-stretch space-y-4">
          <div className="w-full grid grid-cols-1 gap-4">
            <div className="field-box">
              <Controller
                name="team"
                control={control}
                rules={{ required: 'Team set is required.' }}
                render={({ field, fieldState }) => (
                  <>
                    <label htmlFor={field.name} className="mb-2">
                      Team <span className="text-red-500">*</span>
                    </label>
                    <Dropdown
                      value={field.value}
                      pt={DropdownStyle}
                      filter
                      onChange={e => field.onChange(e.value)}
                      className={classNames({ error: fieldState.error })}
                      options={ComplianceTeamOptions}
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Select Compliance Team"
                      emptyMessage="&nbsp;&nbsp;No Compliance Teams"
                    />
                    {getFormErrorMessage(errors, field.name)}
                  </>
                )}
              />
            </div>
            <div className="field-box">
              <Controller
                name="description"
                control={control}
                rules={{ required: 'Description is required.' }}
                render={({ field, fieldState }) => (
                  <>
                    <label htmlFor={field.name} className="mb-2">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <Dropdown
                      value={field.value}
                      pt={DropdownStyle}
                      filter
                      onChange={e => field.onChange(e.value)}
                      className={classNames({ error: fieldState.error })}
                      options={ComplianceDescOptions}
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Select Description"
                      emptyMessage="&nbsp;&nbsp;No Descriptions"
                    />
                    {getFormErrorMessage(errors, field.name)}
                  </>
                )}
              />
            </div>
          </div>
          <div className="w-full col-span-8 pt-6 flex items-center justify-end space-x-2">
            <Button type="submit" label="Save" loading={loadingCaseCount} />
            <CloseButton
              label="Close"
              onClick={() => {
                setSelectedRun(undefined)
              }}
            />
          </div>
        </form>
      </Dialog>

      <Dialog
        header="Confirm Organization Change"
        visible={!!confirmData}
        closeOnEscape
        onHide={() => {
          if (!confirmData) return
          setConfirmData(undefined)
        }}
        style={{ width: '450px' }}>
        <div className="flex flex-col space-y-4 p-2">
          <p className="text-base">
            {confirmData && confirmData.caseCount > 0
              ? `This will update the organization and compliance description for all ${confirmData.caseCount} case(s) in run "${selectedRun?.run}".`
              : `This will update the organization and compliance description for all cases in run "${selectedRun?.run}".`}
          </p>
          <div className="flex items-center justify-end space-x-2 pt-4">
            <Button
              label="Confirm"
              loading={updatingCompliance}
              onClick={handleConfirmedComplianceUpdate}
            />
            <CloseButton
              label="Cancel"
              onClick={() => {
                setConfirmData(undefined)
              }}
            />
          </div>
        </div>
      </Dialog>
    </div>
  )
}

export default CfdReport
